
import { Check } from "lucide-react";
import { PricingConfigType } from "@/hooks/subscription/types";
import { useNavigate } from "react-router-dom";

interface FamilyPlanCardProps {
  billingPeriod: "monthly" | "yearly";
  handleGetStarted: () => void;
  pricingConfig: PricingConfigType;
}

const FamilyPlanCard = ({
  billingPeriod,
  handleGetStarted: _handleGetStarted,
  pricingConfig
}: FamilyPlanCardProps) => {
  const isYearly = billingPeriod === "yearly";

  // Calculate price display - show monthly equivalent for annual like subscription page
  const familyPrice = isYearly
    ? `$${(pricingConfig.family.annual.amount / 100 / 12).toFixed(2)}`
    : pricingConfig.family.monthly.formattedPrice;

  // Calculate billing text
  const billingText = isYearly
    ? `per month, billed annually (${pricingConfig.family.annual.formattedPrice}/year)`
    : "/month";

  const navigate = useNavigate();
  const handleContactClick = () => {
    navigate('/contact');
    window.scrollTo(0, 0);
  };
  return <div className="bg-white rounded-lg shadow-md border-2 border-blue-500 overflow-hidden h-full flex flex-col relative">
      <div className="absolute top-0 right-0 bg-blue-500 text-white px-2 sm:px-3 py-1 rounded-bl-lg text-xs sm:text-sm font-medium">
        Family
      </div>
      <div className="p-5 sm:p-6 flex-grow">
        <h3 className="text-lg sm:text-xl font-bold">Family</h3>
        <p className="text-neutral-600 text-xs sm:text-sm mt-1">For the whole family</p>
        
        <div className="mt-4 sm:mt-6 mb-2">
          <span className="text-2xl sm:text-3xl font-bold">
            {familyPrice}
          </span>
          <span className="text-neutral-500 text-xs sm:text-sm ml-1">
            {isYearly ? "" : "/month"}
          </span>
        </div>
        {isYearly && (
          <div className="text-xs sm:text-sm text-neutral-600 mb-2">
            {billingText}
          </div>
        )}
        {isYearly && <p className="text-xs sm:text-sm text-green-600">
            Save {pricingConfig.annual.savingsPercentage}% compared to monthly
          </p>}
        
        <div className="mt-4 sm:mt-6 mb-3">
          <p className="font-medium text-sm sm:text-base">Families who train together:</p>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <Check className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
            </div>
            <p className="ml-2 sm:ml-3 text-sm sm:text-base text-neutral-700 text-left">Everything in Premium for Two Adults and Two Children</p>
          </div>
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <Check className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
            </div>
            <p className="ml-2 sm:ml-3 text-sm sm:text-base text-neutral-700 text-left">Additional Child Profiles for $5.99/mo or $59.99/yr ea</p>
          </div>
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <Check className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
            </div>
            <p className="ml-2 sm:ml-3 text-sm sm:text-base text-neutral-700 text-left">One Login With Multiple Profiles</p>
          </div>
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <Check className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
            </div>
            <p className="ml-2 sm:ml-3 text-sm sm:text-base text-neutral-700 text-left">Shared Family Progress Dashboard</p>
          </div>
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <Check className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
            </div>
            <p className="ml-2 sm:ml-3 text-sm sm:text-base text-neutral-700 text-left">Family Training Sessions</p>
          </div>
        </div>
        
        <p className="text-xs sm:text-sm text-neutral-600 mt-4 sm:mt-6 italic">
          (Families who train together, grow together.)
        </p>
      </div>
      
      <div className="px-5 sm:px-8 pb-5 sm:pb-8">
        <button onClick={handleContactClick} className="w-full bg-black hover:bg-neutral-800 text-white py-2 px-4 rounded-md flex items-center justify-center transition-colors text-sm sm:text-base">
          Contact Us
          <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </button>
      </div>
    </div>;
};

export default FamilyPlanCard;
